<template>
  <div class="w-full">
    <button
      type="button"
      :class="buttonClass"
      :disabled="loading"
      @click="login"
    >
      <svg v-if="!loading" class="h-5 w-5 shrink-0" :class="variant === 'primary' ? 'me-2.5' : 'mr-3'" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
        <path
          d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
          fill="#4285F4"
        />
        <path
          d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
          fill="#34A853"
        />
        <path
          d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
          fill="#FBBC05"
        />
        <path
          d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
          fill="#EA4335"
        />
      </svg>
      <Loader v-if="loading" class="animate-spin h-5 w-5 shrink-0" :class="variant === 'primary' ? 'me-2.5' : 'mr-3'" />
      <span :class="variant === 'primary' ? 'text-base font-semibold' : 'text-lg'">{{ loading ? $t('loading') + '...' : $t('auth.continue_with_google') }}</span>
    </button>
    <div v-if="error" class="mt-2 text-red-500 text-sm text-center">{{ error }}</div>
  </div>
</template>

<script setup>
import { ref, computed, defineProps, defineEmits } from 'vue'
import { useAuthStore } from '@/stores/auth'
import { Loader } from 'lucide-vue-next'
import { useI18n } from 'vue-i18n'
import { getApiErrorMessage } from '@/utils/error-handler'

const props = defineProps({
  redirect: { type: String, default: null },
  /** Full path passed to `loginWithGoogle({ redirectPath })` (e.g. meal plan return URL). */
  redirectPath: { type: String, default: null },
  /** Called immediately before redirecting to Google (e.g. save wizard draft). */
  beforeLogin: { type: Function, default: null },
  /** `default` = outlined Google style; `primary` = brand gradient pill (meal plan gate). */
  variant: {
    type: String,
    default: 'default',
    validator: (v) => ['default', 'primary'].includes(v)
  }
})

const emit = defineEmits(['error'])

const { t } = useI18n()
const loading = ref(false)
const error = ref('')
const auth = useAuthStore()

const buttonClass = computed(() => {
  if (props.variant === 'primary') {
    return 'flex w-full cursor-pointer items-center justify-center rounded-full bg-gradient-to-r from-indigo-600 to-pink-600 px-4 py-4 text-base font-semibold text-white shadow-lg transition hover:from-indigo-700 hover:to-pink-700 disabled:cursor-not-allowed disabled:opacity-50'
  }
  return 'w-full flex justify-center items-center py-3 px-4 rounded-lg border-2 border-blue-500 dark:border-blue-400 bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-100 font-bold hover:bg-blue-50 dark:hover:bg-blue-900/20 cursor-pointer shadow-md hover:shadow-lg transform hover:scale-[1.02] transition-all duration-200 animate-pulse-subtle'
})

async function login() {
  error.value = ''
  loading.value = true
  try {
    if (typeof props.beforeLogin === 'function') {
      await props.beforeLogin()
    }
    const opts = props.redirectPath ? { redirectPath: props.redirectPath } : {}
    await auth.loginWithGoogle(opts)
  } catch (err) {
    console.error('Google login error:', err)
    error.value = getApiErrorMessage(err, t, 'auth.unknown_error')
    emit('error', error.value)
  } finally {
    loading.value = false
  }
}
</script>

<style scoped>
.animate-pulse-subtle {
  animation: pulse-subtle 2s infinite;
}

@keyframes pulse-subtle {
  0% {
    box-shadow: 0 0 0 0 rgba(59, 130, 246, 0.3);
  }
  70% {
    box-shadow: 0 0 0 6px rgba(59, 130, 246, 0);
  }
  100% {
    box-shadow: 0 0 0 0 rgba(59, 130, 246, 0);
  }
}
</style>
